"use client";

// ===================================================
// GamTable — THE universal table. One engine, every table in the system.
//
// Self-sufficient: it reads the global cc_table_settings and applies every one
// of them (density, zebra, sticky header, accent) on its OWN markup — it does
// not depend on an outer TableSurface to win a CSS specificity war with shadcn's
// default cell/row utilities (which is exactly why "change it once, all follow"
// silently didn't work before). A per-table ⚙ menu overrides skin + accent
// locally, on top of the global default (Gal: "גלובלי + דריסה מקומית").
//
// Config-over-code: a new table is a `columns` array. Mark a column
// `meta: { filterable: true }` and it gets the multi-select chip filter — the
// exact pattern Gal already built in AiResourceTable, lifted here so it is not
// rebuilt a fourth time (Rule 10).
// ===================================================

import { useEffect, useMemo, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type SortingState,
  type VisibilityState,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ChevronsUpDown, Search, SlidersHorizontal, Palette, X } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/shadcn/table";
import { Input } from "@/components/shadcn/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/shadcn/dropdown-menu";
import { Button } from "@/components/shadcn/button";
import { cn } from "@/lib/utils";
import { useFilterColumns, useUpsertFilterColumn } from "@/lib/vdev-filter-queries";
import {
  ACCENT_VARS,
  ACCENT_LABELS,
  TABLE_SETTINGS_DEFAULTS,
  useTableSettings,
  type TableAccent,
  type TableSkin,
} from "@/lib/table-settings";

// column meta contract — opt a column into the chip filter
export interface GamColumnMeta {
  filterable?: boolean;
  /** Multi-valued cell (e.g. string[]). The filter matches ANY. */
  multi?: boolean;
  /** Start hidden. A persisted choice (surface) still wins over this. */
  defaultHidden?: boolean;
}

export interface GamTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  getRowId?: (row: TData) => string;
  onRowClick?: (row: TData) => void;
  freezeFirst?: boolean;
  searchPlaceholder?: string;
  pageSize?: number;
  emptyText?: string;
  /** Extra section rendered INSIDE the existing "מראה" (appearance) popover,
   *  below the accent swatches — not a new button in the toolbar. Gal's call
   *  (2026-08-01): consumer-specific controls (e.g. a detail-panel side
   *  toggle) live in the one menu that already exists, not beside it. */
  paletteMenuExtra?: React.ReactNode;
  /** Rendered first in the toolbar row, before search — e.g. a back button +
   *  the entity name, matching the instance engine's own header convention
   *  (VNoteMobileNav's breadcrumb bar: ArrowRight + title). Folds what used
   *  to be a separate page <h1> into the one toolbar row (Gal, 2026-08-01). */
  toolbarStart?: React.ReactNode;
  /** Rendered last in the toolbar row, after מראה/עמודות — e.g. the
   *  "N מתוך M" count, on the far side from the entity name. */
  toolbarEnd?: React.ReactNode;
  /** Optional bounded server mode. Rows are already filtered and paginated;
   *  GamTable keeps its chrome while delegating search/page changes upstream. */
  server?: {
    total: number;
    pageIndex: number;
    pageCount: number;
    search: string;
    onSearchChange: (value: string) => void;
    onPageChange: (pageIndex: number) => void;
  };
  /** Persist the "עמודות" show/hide choices under this name in
   *  `vdev_filter_columns` (tenant `gam`, one row per column), so they
   *  survive refresh — the same table FilterShell already uses for its
   *  facet panes, keyed by `surface` (Rule 10: no second preferences store).
   *  Without it the menu keeps its old in-memory behaviour. Gal's ask
   *  (2026-09-05): one shell that works the same in every table. */
  surface?: string;
}

const SKIN_OPTS: { v: TableSkin; label: string }[] = [
  { v: "quiet", label: "שקט" },
  { v: "pro", label: "מקצועי" },
  { v: "signature", label: "ייחודי" },
  { v: "soft", label: "רך" },
];

// Root cause (2026-08-01): shadcn's `bg-popover` utility has no matching
// `--color-popover` entry in this repo's Tailwind v4 `@theme inline` block
// (src/app/globals.css ~L130) — the class resolves to nothing, so every
// dropdown menu was fully see-through onto whatever sat behind it. 6 files
// repo-wide share the bug; fixing the shared token is a separate, wider-
// blast-radius change (flagged as task_7cb2161f, not done here). This is
// the standard override for every popover THIS table opens — reuses the
// chrome frosted-glass recipe already established for TopBar/Sidebar
// ("--bg-chrome, with blur") instead of inventing a new surface color.
// Gal's call (2026-08-01): every dropdown this table adds gets this, not
// just the one that happened to be reported first.
const MENU_SURFACE_CLASS =
  "border-white/10 bg-[rgba(20,20,20,0.92)] shadow-lg backdrop-blur-xl backdrop-saturate-150";

function densityPad(density: string, head = false) {
  if (density === "compact") return head ? "px-2.5 py-1.5" : "px-2.5 py-1.5";
  return head ? "px-3.5 py-2" : "px-3.5 py-2.5";
}

export function Chip({
  children,
  active,
  onClick,
}: {
  children: React.ReactNode;
  active?: boolean;
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        "rounded-full border px-2 py-0.5 text-[11px] transition-colors",
        active
          ? "border-[rgba(var(--table-accent-rgb),0.5)] bg-[rgba(var(--table-accent-rgb),0.22)] text-white"
          : "border-white/10 bg-white/5 text-slate-300 hover:border-white/25",
      )}
    >
      {children}
    </button>
  );
}

export function GamTable<TData>({
  data,
  columns,
  getRowId,
  onRowClick,
  freezeFirst = true,
  searchPlaceholder = "חיפוש בכל העמודות…",
  pageSize = 25,
  emptyText = "אין תוצאות",
  paletteMenuExtra,
  toolbarStart,
  toolbarEnd,
  server,
  surface,
}: GamTableProps<TData>) {
  const { data: settings = TABLE_SETTINGS_DEFAULTS } = useTableSettings();

  // local (per-table) overrides — null means "use the global setting"
  const [localSkin, setLocalSkin] = useState<TableSkin | null>(null);
  const [localAccent, setLocalAccent] = useState<TableAccent | null>(null);
  const skin = localSkin ?? settings.skin;
  const accent = localAccent ?? settings.accent;
  const density = settings.density;

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(() => {
    const init: VisibilityState = {};
    for (const c of columns) {
      const id = c.id ?? (c as { accessorKey?: string }).accessorKey;
      if (id && (c.meta as GamColumnMeta | undefined)?.defaultHidden) init[id] = false;
    }
    return init;
  });

  // persisted column visibility (only when the consumer named a surface)
  const persistedCols = useFilterColumns(surface ?? "", { enabled: !!surface });
  const upsertCol = useUpsertFilterColumn();
  useEffect(() => {
    if (!surface || !persistedCols.data) return;
    const fromDb: VisibilityState = {};
    for (const row of persistedCols.data) fromDb[row.col_key] = row.visible;
    // DB rows win for the columns they name; columns with no row stay visible.
    setColumnVisibility((prev) => ({ ...prev, ...fromDb }));
  }, [surface, persistedCols.data]);

  const onToggleColumn = (col: { id: string; columnDef: ColumnDef<TData, unknown>; toggleVisibility: (v: boolean) => void }, visible: boolean) => {
    col.toggleVisibility(visible);
    if (!surface) return;
    const def = col.columnDef as ColumnDef<TData, unknown> & { accessorKey?: string };
    upsertCol.mutate({
      surface,
      col_key: col.id,
      label: typeof def.header === "string" ? def.header : col.id,
      source_field: def.accessorKey ?? col.id,
      visible,
    });
  };
  // chip filters: columnId -> set of allowed string values
  const [facets, setFacets] = useState<Record<string, Set<string>>>({});

  // which columns opted into the chip filter, and their distinct values
  const filterableCols = useMemo(
    () => server ? [] : columns.filter((c) => (c.meta as GamColumnMeta | undefined)?.filterable),
    [columns, server],
  );

  const facetOptions = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const col of filterableCols) {
      const id = (col.id ?? (col as { accessorKey?: string }).accessorKey) as string;
      const meta = col.meta as GamColumnMeta | undefined;
      const vals = new Set<string>();
      for (const row of data) {
        const raw = (row as Record<string, unknown>)[id];
        if (meta?.multi && Array.isArray(raw)) raw.forEach((v) => v != null && vals.add(String(v)));
        else if (raw != null && raw !== "") vals.add(String(raw));
      }
      out[id] = [...vals].sort();
    }
    return out;
  }, [filterableCols, data]);

  const toggleFacet = (id: string, val: string) =>
    setFacets((prev) => {
      const next = new Set(prev[id] ?? []);
      next.has(val) ? next.delete(val) : next.add(val);
      return { ...prev, [id]: next };
    });

  const clearFacets = () => setFacets({});
  const activeFacetCount = Object.values(facets).reduce((n, s) => n + s.size, 0);

  // apply the chip filters on top of tanstack's global text filter
  const filteredData = useMemo(() => {
    const active = Object.entries(facets).filter(([, s]) => s.size > 0);
    if (active.length === 0) return data;
    return data.filter((row) =>
      active.every(([id, set]) => {
        const raw = (row as Record<string, unknown>)[id];
        if (Array.isArray(raw)) return raw.some((v) => set.has(String(v)));
        return set.has(String(raw));
      }),
    );
  }, [data, facets]);

  const table = useReactTable({
    data: filteredData,
    columns,
    state: {
      sorting,
      globalFilter: server ? "" : globalFilter,
      columnVisibility,
      ...(server ? { pagination: { pageIndex: server.pageIndex, pageSize } } : {}),
    },
    getRowId,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onColumnVisibilityChange: setColumnVisibility,
    enableSorting: !server,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: Boolean(server),
    pageCount: server?.pageCount,
    onPaginationChange: server
      ? (updater) => {
          const current = { pageIndex: server.pageIndex, pageSize };
          const next = typeof updater === "function" ? updater(current) : updater;
          server.onPageChange(next.pageIndex);
        }
      : undefined,
    initialState: { pagination: { pageSize } },
  });

  const hideableCols = table.getAllColumns().filter((c) => c.getCanHide());
  const rows = table.getRowModel().rows;
  const total = server?.total ?? table.getFilteredRowModel().rows.length;
  const pageStart = (server?.pageIndex ?? table.getState().pagination.pageIndex) * pageSize;

  return (
    <div
      className="gam-table w-full space-y-3"
      dir="rtl"
      data-skin={skin}
      data-density={density}
      data-zebra={settings.zebra ? "on" : "off"}
      data-sticky={settings.sticky_head ? "on" : "off"}
      style={{ "--table-accent-rgb": ACCENT_VARS[accent] } as React.CSSProperties}
    >
      {/* toolbar — one row (BRAND.md chrome economy).
         Order (Gal, 2026-08-01): entity name+back → search, centered →
         appearance/columns actions → count, on the far side. */}
      <div className="flex flex-wrap items-center gap-2">
        {toolbarStart}

        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute right-2.5 top-1/2 size-3.5 -translate-y-1/2 text-slate-500" />
          <Input
            value={server?.search ?? globalFilter}
            onChange={(e) => server ? server.onSearchChange(e.target.value) : setGlobalFilter(e.target.value)}
            placeholder={searchPlaceholder}
            className="pr-8"
          />
        </div>

        {/* column show/hide */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <SlidersHorizontal className="size-3.5" />
              עמודות
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            className={cn(MENU_SURFACE_CLASS, "max-h-72 overflow-auto")}
          >
            <DropdownMenuLabel>הצג עמודות</DropdownMenuLabel>
            {hideableCols.map((col) => (
              <DropdownMenuCheckboxItem
                key={col.id}
                checked={col.getIsVisible()}
                onCheckedChange={(v) => onToggleColumn(col, !!v)}
              >
                {typeof col.columnDef.header === "string" ? col.columnDef.header : col.id}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        {/* per-table appearance override (⚙ palette) */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm" className="gap-2">
              <Palette className="size-3.5" />
              מראה
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="center"
            className={cn(MENU_SURFACE_CLASS, "w-64 space-y-3 p-3")}
          >
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Skin לטבלה זו</p>
              <div className="flex flex-wrap gap-1.5">
                {SKIN_OPTS.map((s) => (
                  <Chip key={s.v} active={skin === s.v} onClick={() => setLocalSkin(s.v)}>
                    {s.label}
                  </Chip>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">צבע הדגשה</p>
              <div className="flex flex-wrap gap-1.5">
                {(Object.keys(ACCENT_VARS) as TableAccent[]).map((a) => (
                  <button
                    key={a}
                    title={ACCENT_LABELS[a]}
                    onClick={() => setLocalAccent(a)}
                    className={cn(
                      "size-5 rounded-full border transition",
                      accent === a ? "border-white ring-2 ring-white/30" : "border-white/20",
                    )}
                    style={{ background: `rgb(${ACCENT_VARS[a]})` }}
                  />
                ))}
              </div>
            </div>
            {(localSkin || localAccent) && (
              <button
                onClick={() => {
                  setLocalSkin(null);
                  setLocalAccent(null);
                }}
                className="flex items-center gap-1 text-[11px] text-slate-400 hover:text-teal-200"
              >
                <X className="size-3" /> חזרה לברירת-המחדל הגלובלית
              </button>
            )}
            {paletteMenuExtra && (
              <div className="space-y-1.5 border-t border-white/10 pt-3">
                {paletteMenuExtra}
              </div>
            )}
          </DropdownMenuContent>
        </DropdownMenu>

        <div className="ms-auto">
          {toolbarEnd ?? (
            <span className="text-xs text-slate-500">{total.toLocaleString()} שורות</span>
          )}
        </div>
      </div>

      {/* chip filters — the adopted AiResourceTable pattern */}
      {filterableCols.length > 0 && (
        <div className="space-y-2 rounded-xl border border-white/10 bg-white/[0.02] p-3">
          {filterableCols.map((col) => {
            const id = (col.id ?? (col as { accessorKey?: string }).accessorKey) as string;
            const opts = facetOptions[id] ?? [];
            if (opts.length === 0) return null;
            return (
              <div key={id} className="flex flex-wrap items-center gap-1.5">
                <span className="min-w-20 text-[11px] font-medium text-slate-500">
                  {typeof col.header === "string" ? col.header : id}
                </span>
                {opts.slice(0, 40).map((o) => (
                  <Chip key={o} active={facets[id]?.has(o)} onClick={() => toggleFacet(id, o)}>
                    {o}
                  </Chip>
                ))}
              </div>
            );
          })}
          {activeFacetCount > 0 && (
            <button onClick={clearFacets} className="text-[11px] text-slate-400 hover:text-teal-200">
              נקה סינון ({activeFacetCount})
            </button>
          )}
        </div>
      )}

      {/* the table */}
      <div className="overflow-auto rounded-[var(--radius-card)] border border-white/10 max-h-[calc(100vh-340px)]">
        <Table className="w-max min-w-full border-separate border-spacing-0">
          <TableHeader>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((h, i) => {
                  const frozen = freezeFirst && i === 0;
                  const sortDir = h.column.getIsSorted();
                  const canSort = h.column.getCanSort();
                  return (
                    <TableHead
                      key={h.id}
                      onClick={canSort ? h.column.getToggleSortingHandler() : undefined}
                      className={cn(
                        densityPad(density, true),
                        "text-start text-[11px] font-semibold uppercase tracking-wide text-slate-400 whitespace-nowrap",
                        canSort && "cursor-pointer select-none hover:text-slate-200",
                        frozen && "sticky-frozen right-0 z-20",
                      )}
                    >
                      <span className="inline-flex items-center gap-1.5">
                        {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                        {canSort &&
                          (sortDir === "asc" ? (
                            <ArrowUp className="size-3" />
                          ) : sortDir === "desc" ? (
                            <ArrowDown className="size-3" />
                          ) : (
                            <ChevronsUpDown className="size-3 opacity-40" />
                          ))}
                      </span>
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  {emptyText}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                  className={cn("group border-b border-white/[0.06]", onRowClick && "cursor-pointer")}
                >
                  {row.getVisibleCells().map((cell, i) => {
                    const frozen = freezeFirst && i === 0;
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          densityPad(density),
                          "text-start align-middle",
                          frozen && "sticky-frozen right-0 z-10 bg-[#141414] shadow-[-1px_0_0_rgba(42,42,42,1)] group-hover:bg-[#181818]",
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* pagination */}
      {(server ? server.pageCount > 1 : table.getPageCount() > 1) && (
        <div className="flex items-center justify-between text-xs text-slate-500">
          <span>
            {(pageStart + 1).toLocaleString()}–{Math.min(pageStart + rows.length, total).toLocaleString()} מתוך{" "}
            {total.toLocaleString()}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
              הקודם
            </Button>
            <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              הבא
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
